import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Music, Download, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from '@/lib/utils';
import { Sequence } from '@/types/sequence';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useFavorites } from '@/contexts/FavoritesContext';
import AuthModal from '@/components/auth/AuthModal';
import { useFavoritesStore } from '@/stores/useFavoritesStore';
import { useToastStore } from '@/stores/useToastStore';

interface SequenceCardProps {
  sequence: Sequence;
  toggleFavorite?: (id: string) => void;
  isFavorite?: boolean;
}

const SAINT_NICK_USER_ID = 'f47ac10b-58cc-4372-a567-0e02b2c3d479';

export const SequenceCard: React.FC<SequenceCardProps> = ({ 
  sequence, 
  toggleFavorite: externalToggleFavorite,
  isFavorite: externalIsFavorite = false 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [currentRating, setCurrentRating] = useState(sequence.averageRating || sequence.rating || 0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { isSequenceFavorite, toggleSequenceFavorite } = useFavorites();
  const { addFavorite, removeFavorite, isFavorite: isFavoriteStore } = useFavoritesStore();
  const addToast = useToastStore((state) => state.addToast);
  
  // Image URL fallback logic
  const getImageUrl = () => {
    // First check for direct imageUrl
    if (sequence.imageUrl && sequence.imageUrl.trim() !== '') {
      return sequence.imageUrl;
    }
    
    // Check if sequence has song imageUrl
    if (sequence.song?.imageUrl && sequence.song.imageUrl.trim() !== '') {
      return sequence.song.imageUrl;
    }
    
    // Return null for placeholder
    return null;
  };

  const imageUrl = getImageUrl();

  const handleImageError = () => {
    // Image error handled silently - logging removed to reduce console noise
    setImageLoadError(true);
  };

  const handleImageLoad = () => {
    setIsLoaded(true);
    setImageLoadError(false);
  };

  const isFree = sequence.isFree || sequence.price === 0;
  const isFavorite = externalIsFavorite || isSequenceFavorite(sequence.id) || isFavoriteStore(sequence.id);

  const handleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (externalToggleFavorite) {
      externalToggleFavorite(sequence.id);
    } else {
      if (isFavorite) {
        removeFavorite(sequence.id);
        addToast({
          title: 'Removed from favorites',
          description: `${sequence.title || 'Sequence'} has been removed from your favorites`,
          type: 'info'
        });
      } else {
        addFavorite({
          id: sequence.id,
          title: sequence.title || 'Untitled Sequence',
          imageUrl: sequence.imageUrl,
          type: 'sequence'
        });
        addToast({
          title: 'Added to favorites',
          description: `${sequence.title || 'Sequence'} has been added to your favorites`,
          type: 'success'
        });
      }
    }
  };

  const handleAuthSuccess = () => {
    // After successful auth, trigger the favorite action
    if (externalToggleFavorite) {
      externalToggleFavorite(sequence.id);
    } else {
      toggleSequenceFavorite(sequence.id);
      toast({
        title: "Added to favorites",
        description: "Sequence added to your favorites"
      });
    }
  };

  const handleRatingUpdate = (newRating: number) => {
    setCurrentRating(newRating);
  };

  const placeholderImage = (
    <div className="w-full h-full flex items-center justify-center bg-muted">
      <div className="text-center p-4">
        <img 
          src="/sequence-cover-placeholder.svg" 
          alt="Sequence cover placeholder" 
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  );

  return (
    <>
      <div 
        className="group relative rounded-xl overflow-hidden bg-card shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => navigate(`/sequence/${sequence.id}`)}
      >
        {/* Album Cover Container */}
        <div className="aspect-square w-full overflow-hidden relative">
          {/* Initial blur overlay while image loads */}
          <div 
            className={cn(
              "absolute inset-0 bg-muted backdrop-blur-sm z-10 transition-opacity duration-500",
              isLoaded ? "opacity-0" : "opacity-100"
            )}
          />
          
          {imageUrl && !imageLoadError ? (
            /* Sequence Cover with loading effect */
            <img
              src={imageUrl}
              alt={`${sequence.title || 'Sequence'} cover`}
              className={cn(
                "w-full h-full object-cover transition-all duration-700",
                isLoaded ? "scale-100 filter-none" : "scale-105 blur-sm",
                isHovered ? "scale-110" : "scale-100"
              )}
              onLoad={handleImageLoad}
              onError={handleImageError}
              loading="lazy"
            />
          ) : placeholderImage}

          {/* Software Badge */}
          <Badge 
            className="absolute top-3 left-3 z-30 bg-white/20 backdrop-blur-sm text-white border-none"
          >
            {sequence.software_type || sequence.software}
          </Badge>
          
          {/* Favorite Button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-3 right-3 z-30 rounded-full bg-black/20 backdrop-blur-sm hover:bg-black/30 text-white"
            onClick={handleFavorite}
          >
            <Heart 
              size={18} 
              className={cn(
                "transition-all",
                isFavorite ? "fill-destructive stroke-destructive" : "fill-none"
              )} 
            />
          </Button>
          
          {/* Price Tag */}
          <div className="absolute bottom-3 right-3 z-30">
            <Badge 
              className={cn(
                "backdrop-blur-sm border-none",
                isFree
                  ? "bg-green-500/80 text-white" 
                  : "bg-white/20 text-white"
              )}
            >
              {isFree ? 'Free' : `$${sequence.price.toFixed(2)}`}
            </Badge>
          </div>

          {/* Hover overlay */}
          <div 
            className={cn(
              "absolute inset-0 bg-black/0 transition-all duration-300",
              isHovered ? "bg-black/30" : ""
            )}
          />
        </div>

        {/* Content */}
        <div className="p-3">
          <div className="mb-1">
            <h3 className="font-medium text-base line-clamp-1">{sequence.title}</h3>
          </div>

          {/* Song Details */}
          <div className="mt-1 mb-2">
            <div className="flex items-center gap-1">
              <Music size={12} className="text-muted-foreground flex-shrink-0" />
              <p className="text-xs text-muted-foreground line-clamp-1">
                {sequence.song?.artist || 'Unknown Artist'}
              </p>
            </div>
          </div>

          {/* Creator Info and Action Button */}
          <div className="flex items-center justify-between">
            {/* Only show profile link if we have a valid user ID */}
            {(sequence.userId || sequence.creatorId) ? (
              <Link 
                to={`/profile/${sequence.userId || sequence.creatorId}`}
                className="flex items-center gap-2 hover:text-primary transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <Avatar className="h-5 w-5">
                  <AvatarImage src={sequence.creatorAvatar} alt={sequence.creatorName || 'Creator'} />
                  <AvatarFallback className="text-xs">{(sequence.creatorName || 'Unknown Creator').charAt(0)}</AvatarFallback>
                </Avatar>
                <span className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                  {sequence.creatorName || 'Sequence Creator'}
                </span>
              </Link>
            ) : (
              /* No clickable link for undefined user IDs */
              <div className="flex items-center gap-2">
                <Avatar className="h-5 w-5">
                  <AvatarFallback className="text-xs">U</AvatarFallback>
                </Avatar>
                <span className="text-xs text-muted-foreground">
                  Unknown Creator
                </span>
              </div>
            )}
            
            <div className="flex items-center gap-1 text-muted-foreground">
              <Download size={12} />
              <span className="text-xs">{sequence.downloads || sequence.downloadCount || 0}</span>
            </div>
          </div>
        </div>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
        title="Sign in to save favorites"
        description="Sign in to save this sequence to your favorites and access it later."
      />
    </>
  );
};
